import {
  Box,
  Button,
  createStyles,
  Grid,
  Link,
  makeStyles,
  Theme,
  Typography
} from "@material-ui/core";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import React from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import Header from "../common/Header";
import Settings from "../common/Settings";
import { APIState } from "../utils/api/reducer";
import { useQueryParams } from "../utils/queryParams";
import Heading from "./Heading";
import PlayerInputs from "./playerInputs";
import { getConfig, setAuth, setPlayers } from "./store/actions";
import { Config, SelectedPlayers } from "./store/types";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      minHeight: "100vh",
      paddingTop: 24,
      backgroundColor: "#102341",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      [theme.breakpoints.down("sm")]: {
        paddingBottom: 0,
      },
    },
    cornerBackground: {
      height: "100%",
      width: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: 100,
      background: "linear-gradient(#015ACE, #102341)",
      clipPath: "polygon(0 0, 453px 0, 0% 430px)",
    },
    item: {
      margin: "24px 0",
      [theme.breakpoints.down("sm")]: {
        margin: "12px 0 6px 0",
      },
      zIndex: 100,
    },
    buttonWrapper: {
      zIndex: 0,
    },
    button: {
      backgroundColor: "#015ACE50",
      padding: 16,
      fontSize: 24,
      fontWeight: 700,
      margin: "12px 0",
      color: theme.palette.common.white,
      borderRadius: 12,
      boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
      "&:hover": {
        backgroundColor: "#015ACE50",
      },
    },
    link: {
      color: "white",
      marginBottom: 8,
      fontFamily: "Roboto",
      textDecoration: "underline",
      fontSize: 20,
    },
    header: {
      fontWeight: 700,
      margin: "24px 0",
      color: theme.palette.common.white,
    },
  })
);

type PlayerCompareParams = {
  readonly league: string;
};

interface ReduxProps {
  readonly selectedPlayers: SelectedPlayers;
  readonly authenticated: APIState<any>;
  readonly config: APIState<Config>;
}

interface DispatchProps {
  readonly SetPlayers: (
    leagueCode: string,
    site: string,
    token: string,
    slate: string
  ) => void;
  readonly SetAuth: (leagueCode: string, token: string) => void;
  readonly GetConfig: (league: string) => void;
}

type Props = ReduxProps & DispatchProps;

const PlayerCompare = (props: Props) => {
  const {
    selectedPlayers,
    config,
    SetPlayers,
    SetAuth,
    GetConfig,
  } = props;
  let { league } = useParams<PlayerCompareParams>();
  let queryParams = useQueryParams();
  const site = queryParams.get("site") || "DK";
  const slate = queryParams.get("slate") || "main";
  const token = queryParams.get("token") || "";

  const classes = useStyles();

  const getHref = () => {
    let side1Query = "";
    let side2Query = "";

    selectedPlayers.side1.forEach((s: string, i) => {
      if (i > 0) {
        side1Query += `+${s}`;
      } else {
        side1Query += s;
      }
    });
    selectedPlayers.side2.forEach((s: string, i) => {
      if (i > 0) {
        side2Query += `+${s}`;
      } else {
        side2Query += s;
      }
    });

    return `/playercompare/${league}/compare?site=${site}&token=${token}&slate=${slate}&side1=${encodeURIComponent(
      side1Query
    )}&side2=${encodeURIComponent(side2Query)}`;
  };

  React.useEffect(() => {
    SetPlayers(league, site, token, slate);
    SetAuth(league, token);
  }, [league, SetPlayers, site, token, SetAuth, slate]);

  React.useEffect(() => {
    GetConfig(league);
  }, []);

  return (
    <div className={classes.root}>
      <Header />
      <div className={classes.cornerBackground}></div>
      <Grid container>
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <Grid item xs={12} className={classes.item}>
            <Typography variant="h4" className={classes.header} align="center">
              {league} DFS Player Compare
            </Typography>
          </Grid>
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Grid item xs={12} className={classes.item}>
            <Grid item xs={12} className={classes.item}>
              <Heading />
            </Grid>
          </Grid>
        </Box>
        <Grid item xs={12} className={classes.item}>
          <Settings config={config} />
        </Grid>
        <Grid item xs={12} className={classes.item}>
          <PlayerInputs />
        </Grid>
        <Grid
          container
          item
          xs={12}
          justifyContent="center"
          className={`${classes.item} ${classes.buttonWrapper}`}
        >
          <Button
            startIcon={<FlashOnIcon />}
            href={getHref()}
            className={classes.button}
            size="large"
            disabled={
              selectedPlayers.side1.length !== selectedPlayers.side2.length ||
              selectedPlayers.side1.length < 1 ||
              selectedPlayers.side2.length < 1
            }
          >
            Compare Players
          </Button>
        </Grid>
        <Grid container item xs={12} justifyContent="center">
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Grid item xs={12} className={classes.item}>
              <Heading />
            </Grid>
          </Box>
          <Link
            href={`/playercompare/${league}/trends?site=${site}&token=${token}`}
            className={classes.link}
          >
            See Trending Comparisons
          </Link>
        </Grid>
      </Grid>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  const { selectedPlayers, authenticated, config } = state.playerCompare;
  return {
    selectedPlayers,
    authenticated,
    config,
  };
};

const mapDispatchToProps = {
  SetPlayers: setPlayers,
  SetAuth: setAuth,
  GetConfig: getConfig,
};

export default connect(mapStateToProps, mapDispatchToProps)(PlayerCompare);
